<script setup lang="ts">
import { computed, ref, watch, nextTick } from "vue";
import { useTranslation } from "i18next-vue";
import {
  useGetCatalogues,
  useGetCataloguePartsTree,
  useGetDisorderTemplates,
} from "@/stores/queries";
import CatalogueSessionState from "@/stores/report/CatalogueSessionState";
import type { PaginationParams } from "@/types";
import type {
  CataloguePart,
  DisorderGrade,
  DisorderTemplate,
  FetchDisorderTemplatesFilters,
} from "@/types/catalogues.types";
import { useSmartSearch } from "@/composable/useSmartSearch";
import DisorderItem from "./DisorderItem.vue";
import PartTreeItem from "./PartTreeItem.vue";

const props = defineProps<{
  selectedDisorderId?: number;
  selectedGradeId?: number;
}>();

const emit = defineEmits<{
  (e: "selectGrade", disorder: DisorderTemplate, grade: DisorderGrade): void;
  (e: "selectDisorder", disorder: DisorderTemplate): void;
}>();

const { t } = useTranslation();

// Catalogue selection
const selectedCatalogueId = ref<number | null>(
  CatalogueSessionState.lastCatalogueId,
);
const selectedPartId = ref<number | null>(CatalogueSessionState.lastPartId);

// Search
const search = ref("");

// Expanded disorder item (only one at a time)
const expandedDisorderId = ref<number | null>(null);

// Get catalogues list
const { data: catalogues, isLoading: isLoadingCatalogues } = useGetCatalogues();

// Get selected catalogue name
const selectedCatalogueName = computed(() => {
  if (!selectedCatalogueId.value || !catalogues.value) return "";
  const catalogue = catalogues.value.find(
    (c) => c.id === selectedCatalogueId.value,
  );
  return catalogue?.name ?? "";
});

// Get parts tree for selected catalogue
const catalogueIdRef = computed(() => selectedCatalogueId.value ?? 0);
const { data: partsTree, isLoading: isLoadingParts } =
  useGetCataloguePartsTree(catalogueIdRef);

// Pagination for disorders
const pagination = computed<PaginationParams>(() => ({
  skip: 0,
  take: 200,
  sortBy: "code",
  order: "asc",
}));

// Filters for disorders
const filters = computed<FetchDisorderTemplatesFilters>(() => {
  const baseFilters: FetchDisorderTemplatesFilters = {};

  // Filter by part if selected
  if (selectedPartId.value) {
    baseFilters.partId = selectedPartId.value;
  }
  // Otherwise filter by catalogue if selected
  else if (selectedCatalogueId.value) {
    baseFilters.catalogueId = selectedCatalogueId.value;
  }

  return baseFilters;
});

// Only fetch if we have a filter
const shouldFetch = computed(
  () => !!selectedPartId.value || !!selectedCatalogueId.value,
);

const { data: disordersData, isLoading: isLoadingDisorders } =
  useGetDisorderTemplates(pagination, filters);

const disorders = computed(() => {
  if (!shouldFetch.value) return [];
  return disordersData.value?.disorders ?? [];
});

// Smart search on loaded disorders
const { filtered } = useSmartSearch(search, disorders, [
  "title",
  "description",
  "code",
]);

// Handle catalogue change
const handleCatalogueChange = (catalogueId: number | null) => {
  selectedCatalogueId.value = catalogueId;
  selectedPartId.value = null;
  CatalogueSessionState.setCatalogue(catalogueId);
  expandedDisorderId.value = null;
};

// Handle part selection
const handlePartSelect = (partId: number | null) => {
  selectedPartId.value = partId;
  CatalogueSessionState.setPart(partId);
  expandedDisorderId.value = null;
};

// Toggle part expansion in tree
const togglePartExpanded = (partId: number) => {
  CatalogueSessionState.togglePartExpanded(partId);
};

// Check if part is expanded
const isPartExpanded = (partId: number) => {
  return CatalogueSessionState.isPartExpanded(partId);
};

// Toggle disorder expansion (only one at a time)
const handleToggleDisorder = (disorderId: number) => {
  if (expandedDisorderId.value === disorderId) {
    expandedDisorderId.value = null;
  } else {
    expandedDisorderId.value = disorderId;
  }
};

// Handle grade selection
const handleSelectGrade = (
  disorder: DisorderTemplate,
  grade: DisorderGrade,
) => {
  emit("selectGrade", disorder, grade);
};

// Handle disorder selection (without grade)
const handleSelectDisorder = (disorder: DisorderTemplate) => {
  emit("selectDisorder", disorder);
};

// Count disorders recursively for a part
const countDisordersRecursive = (part: CataloguePart): number => {
  let count = part._count?.disorders ?? 0;
  if (part.children?.length) {
    for (const child of part.children) {
      count += countDisordersRecursive(child);
    }
  }
  return count;
};

// Watch for scroll target from navigation
watch(
  () => CatalogueSessionState.scrollToDisorderId,
  async (disorderId) => {
    if (!disorderId) return;

    // Sync local state with session state (in case navigateToDisorder changed them)
    selectedCatalogueId.value = CatalogueSessionState.lastCatalogueId;
    selectedPartId.value = CatalogueSessionState.lastPartId;

    // Wait for disorders to load and DOM to update
    await nextTick();

    // Small delay to ensure data is loaded
    setTimeout(() => {
      // Expand and scroll to the disorder
      expandedDisorderId.value = disorderId;

      // Scroll to the disorder element
      const element = document.querySelector(
        `[data-disorder-id="${disorderId}"]`,
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      // Clear the scroll target
      CatalogueSessionState.clearScrollTarget();
    }, 300);
  },
  { immediate: true },
);
</script>

<template>
  <div class="catalogue-search-tab h-100 d-flex">
    <!-- Left navigation panel -->
    <div class="nav-panel">
      <!-- Catalogue selector (outside scrollbar) -->
      <div class="catalogue-selector">
        <el-select
          :model-value="selectedCatalogueId ?? undefined"
          @update:model-value="handleCatalogueChange"
          :placeholder="t('report:findings.catalogueTab.selectCatalogue')"
          size="large"
          class="catalogue-select"
          :loading="isLoadingCatalogues"
          clearable
        >
          <template #prefix>
            <el-icon><i class="bi-book"></i></el-icon>
          </template>
          <el-option
            v-for="catalogue in catalogues"
            :key="catalogue.id"
            :label="catalogue.name"
            :value="catalogue.id"
          >
            <div class="d-flex align-items-center gap-2">
              <i class="bi-book"></i>
              <span>{{ catalogue.name }}</span>
            </div>
          </el-option>
        </el-select>
      </div>

      <el-scrollbar class="nav-panel-scroll">
        <div class="nav-panel-content">
          <!-- Parts tree -->
          <div v-if="selectedCatalogueId" class="parts-tree">
            <el-skeleton v-if="isLoadingParts" :rows="6" animated />
            <template v-else-if="partsTree?.length">
              <!-- All parts option -->
              <div
                class="part-item root-part"
                :class="{ active: selectedPartId === null }"
                @click="handlePartSelect(null)"
              >
                <span class="part-name">
                  {{ t("report:findings.catalogueTab.allParts") }}
                </span>
                <span class="catalogue-name-desc">{{
                  selectedCatalogueName
                }}</span>
              </div>

              <!-- Recursive tree rendering -->
              <PartTreeItem
                v-for="part in partsTree"
                :key="part.id"
                :part="part"
                :depth="0"
                :selected-part-id="selectedPartId"
                :is-expanded="isPartExpanded"
                :count-disorders="countDisordersRecursive"
                @select="handlePartSelect"
                @toggle="togglePartExpanded"
              />
            </template>
          </div>
        </div>
      </el-scrollbar>
    </div>

    <!-- Right content area -->
    <div class="content-area">
      <!-- Search input -->
      <div class="search-header">
        <el-input
          v-model="search"
          :placeholder="
            shouldFetch
              ? t('report:findings.catalogueTab.searchPlaceholder')
              : t('report:findings.catalogueTab.searchDisabledPlaceholder')
          "
          :disabled="!shouldFetch"
          clearable
          size="large"
          class="search-input"
        >
          <template #prefix>
            <el-icon><i class="bi-search"></i></el-icon>
          </template>
        </el-input>
      </div>

      <!-- Results -->
      <el-scrollbar class="results-scroll">
        <div class="results-content">
          <!-- Empty state: no filter selected -->
          <div
            v-if="!shouldFetch"
            class="empty-state d-flex flex-column align-items-center justify-content-center py-5"
          >
            <el-icon size="48" class="text-muted mb-3">
              <i class="bi-search"></i>
            </el-icon>
            <p class="h6 text-muted mb-2">
              {{ t("report:findings.catalogueTab.emptyState.title") }}
            </p>
            <p class="small text-muted text-center" style="max-width: 300px">
              {{ t("report:findings.catalogueTab.emptyState.description") }}
            </p>
          </div>

          <!-- Loading -->
          <div v-else-if="isLoadingDisorders">
            <el-skeleton :rows="4" animated />
          </div>

          <!-- No results -->
          <div
            v-else-if="!filtered?.length"
            class="text-center text-muted py-5"
          >
            {{ t("report:findings.catalogueTab.noResults") }}
          </div>

          <!-- Results list -->
          <div v-else class="d-flex flex-column gap-2">
            <DisorderItem
              v-for="disorder in filtered"
              :key="disorder.id"
              :disorder="disorder"
              :is-expanded="expandedDisorderId === disorder.id"
              :selected-grade-id="props.selectedGradeId"
              @toggle="handleToggleDisorder(disorder.id)"
              @select-grade="handleSelectGrade"
              @select-disorder="handleSelectDisorder"
            />
          </div>
        </div>
      </el-scrollbar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.catalogue-search-tab {
  background-color: var(--catalogue-bg);
}

.nav-panel {
  width: 280px;
  min-width: 280px;
  background-color: var(--catalogue-bg);
  border-right: 1px solid var(--catalogue-border);
  display: flex;
  flex-direction: column;
}

.nav-panel-scroll {
  flex: 1;
}

.nav-panel-content {
  padding: 0;
}

.catalogue-selector {
  padding: 12px;
  background-color: var(--catalogue-nav);
  border-bottom: 1px solid var(--catalogue-border);
  flex-shrink: 0;
  min-height: 64px;
  display: flex;
  align-items: center;
}

.catalogue-select {
  width: 100%;

  :deep(.el-select__wrapper) {
    background-color: var(--bs-white) !important;
    border: 1px solid var(--catalogue-border) !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    font-size: 14px;
    height: 40px !important;
    min-height: 40px !important;

    &:hover {
      border-width: 2px !important;
      border-color: var(--catalogue-border) !important;
    }

    &.is-focused {
      border-width: 2px !important;
      border-color: var(--catalogue-border) !important;
      box-shadow: none !important;
    }
  }

  :deep(.el-select__prefix) {
    color: var(--catalogue-text);
  }
}

.parts-tree {
  // No border or border-radius
}

.part-item.root-part {
  background-color: var(--catalogue-nav);
  border-bottom: 1px solid var(--catalogue-border);
  padding: 10px 12px;
  font-weight: 500;
  cursor: pointer;
  color: var(--catalogue-text);
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:hover {
    background-color: var(--catalogue-hover);
  }

  &.active {
    background-color: var(--catalogue-border);
    color: var(--bs-dark);
  }
}

.catalogue-name-desc {
  font-size: 11px;
  font-weight: 400;
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.search-header {
  padding: 12px 16px;
  background-color: var(--catalogue-nav);
  border-bottom: 1px solid var(--catalogue-border);
  flex-shrink: 0;
  min-height: 64px;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;

  :deep(.el-input__wrapper) {
    background-color: var(--bs-white) !important;
    border: 1px solid var(--catalogue-border) !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    height: 40px !important;

    &:hover {
      border-width: 2px !important;
      border-color: var(--catalogue-border) !important;
    }

    &.is-focus {
      border-width: 2px !important;
      border-color: var(--catalogue-border) !important;
      box-shadow: none !important;
    }
  }

  :deep(.el-input__prefix) {
    color: var(--catalogue-text);
  }
}

.results-scroll {
  flex: 1;
  background-color: var(--catalogue-bg);
}

.results-content {
  padding: 16px;
}

.empty-state {
  min-height: 200px;
  color: var(--catalogue-text);
}
</style>
